import React, { useContext, createContext, useEffect, useMemo } from 'react';

import PropTypes from 'prop-types';

import { useCurrentProject } from '@adsk/alloy-provider';
import { useLocalState } from '@adsk/alloy-react-helpers';

import usePermission from './usePermission';
import useTerminologies from './useTerminologies';
import { getLocalStatePermissionsKey } from '../helpers/consts';

const CostContext = createContext({});
const useCostContext = () => useContext(CostContext);

const CostProvider = ({ children }) => {
  const currentProject = useCurrentProject();
  const projectId = useMemo(() => currentProject?.id, [currentProject]);

  const productAllowed = useMemo(
    () =>
      projectId &&
      currentProject?.products.some((product) => product.key === 'cost'),
    [currentProject, projectId],
  );

  const { terminologies } = useTerminologies(productAllowed);
  const { permission } = usePermission(productAllowed);
  const permissionKey = useMemo(
    () => getLocalStatePermissionsKey(projectId),
    [projectId],
  );
  const [, setPermission] = useLocalState(permissionKey, null);

  useEffect(() => {
    permission && setPermission(permission);
    return () => {
      try {
        localStorage.removeItem(permissionKey);
      } catch (e) {
        console.error(e);
      }
    };
  }, [permission, permissionKey, setPermission]);

  return (
    <CostContext.Provider value={{ terminologies }}>
      {children}
    </CostContext.Provider>
  );
};

CostProvider.displayName = 'CostProvider';

CostProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

export { useCostContext, CostProvider, CostContext };
